import { useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import IconLoader from '../../icons/loader';
import formatDate from '../../../helpers/format-date';
import SidepanelTextRow from '../sidepanel-text-row';
import SidepanelSelectRow from '../sidepanel-select-row';
import SidepanelDateRow from '../sidepanel-date-row';
import { SelectItemProps } from '../../select/types';
import {
  Account,
  Balance,
  BaseellaStatus,
  Currency,
  ExternalAccount,
  IncomingTransactionGeneral,
  IncomingTransactionOrder,
} from '../types';
import SidepanelTextareaRow from '../sidepanel-textarea-row';
import SidepanelAutocompleteRow from '../sidepanel-autocomplete-row';
import InputText from '../../inputs/input-text';
import formatBalance from '../../../helpers/format-balance';
import ButtonPseudo from '../../button/button-pseudo';
import IconRefresh from '../../icons/refresh';
import SidepanelDefaultRow from '../../side-panel/side-panel-default-row';
import CheckboxInput from '../../checkbox/checkbox';
import { Pagination, PermissionEnum } from '../../../base-types';
import SidepanelAmountRow from '../sidepanel-amount-row';
import IconCopy from '../../icons/copy-two';
import ExternalAccountRow from '../external-account-row';

import styles from '../transaction.module.css';

interface IncomingGeneralContentProps {
  general: IncomingTransactionGeneral;
  isLoading: boolean;
  enableEdit?: boolean;
  currencies?: SelectItemProps[];
  recipientAccounts?: SelectItemProps[];
  pspPartnerAccounts?: SelectItemProps[];
  pspPartners?: SelectItemProps[];
  paymentsTypes?: SelectItemProps[];
  control?: Control;
  errors?: FieldErrors<IncomingTransactionGeneral & IncomingTransactionOrder>;
  isEditable?: boolean;
  onSearchAutocomplete?: (name: string, search: string) => void;
  pspPartnersIsLoading?: boolean;
  recipientAccountsIsLoading?: boolean;
  pspPartnerAccountsIsLoading?: boolean;
  pspPartnerIsFilled?: boolean;
  paymentTypeIsFilled?: boolean;
  baseellaStatus?: BaseellaStatus;
  isCreateForm?: boolean;
  feeText?: string;
  accountBalance?: number;
  currencyId?: string;
  updateBalance?: () => void;
  balanceIsLoading?: boolean;
  currencyName?: string;
  pspPartnerName?: string;
  updatePspPartnerBalance?: () => void;
  pspPartnerBalanceIsLoading?: boolean;
  pspPartnerAccountBalance?: number;
  transferDate?: string;
  withTransactionUrl?: boolean;
  transactionUrlType?: string;
  transactionId?: string;
  activePermission?: PermissionEnum[];
  amount?: string;
  setAmount?: (data?: string) => void;
  setValue?: UseFormSetValue<any>;
  balances?: Balance[];
  accountBalancesIsLoading?: boolean;
  currenciesList?: Currency[];
  fields?: any;
  externalAccounts?: ExternalAccount[];
  externalAccountsIsLoading?: boolean;
  updateExternalBalance?: () => void;
  onSearchExternalAccounts?: (data: string) => void;
  onClearExternalAccounts?: () => void;
  onGetExternalAccountsListAction?: (data: {
    search?: string;
    cursor?: string;
  }) => void;
  externalAccountsPagination?: Pagination;
  externalAccountsNextPageIsLoading?: boolean;
  overdraftEnabled?: boolean;
  recipientAccountsWithInfo?: Account[];
  pspPartnersAccountsList?: any[];
  pspPartnerAccountBalanceInfo?: Balance;
  pspPartnerAccountsPagination?: Pagination;
  pspPartnerAccountsNextPageIsLoading?: boolean;
  pspPartnerAccountsOnScroll?: () => void;
  clearErrors?: UseFormClearErrors<
    IncomingTransactionGeneral & IncomingTransactionOrder
  >;
  setWithTime?: {
    setTransferDateWithTime: (data: boolean) => void;
    setPostingDateWithTime: (data: boolean) => void;
    setAcceptedInWithTime: (data: boolean) => void;
  };
  withTime?: {
    transferDateWithTime?: boolean;
    postingDateWithTime?: boolean;
    accpetedInWithTime?: boolean;
  };
}

export default function IncomingGeneralContent({
  general,
  isLoading,
  currencies,
  recipientAccounts,
  pspPartnerAccounts,
  paymentsTypes,
  control,
  errors,
  isEditable,
  baseellaStatus,
  onSearchAutocomplete,
  updatePspPartnerBalance,
  pspPartnerBalanceIsLoading,
  pspPartnerAccountBalance,
  recipientAccountsIsLoading,
  pspPartnersIsLoading,
  pspPartnerAccountsIsLoading,
  pspPartnerIsFilled,
  updateBalance,
  paymentTypeIsFilled,
  pspPartners,
  feeText,
  currencyId,
  balanceIsLoading,
  accountBalance,
  currencyName,
  pspPartnerName,
  transferDate,
  withTransactionUrl,
  transactionUrlType,
  transactionId,
  activePermission,
  amount,
  setAmount,
  setValue,
  balances,
  accountBalancesIsLoading,
  currenciesList,
  fields,
  externalAccounts,
  externalAccountsIsLoading,
  updateExternalBalance,
  onSearchExternalAccounts,
  onClearExternalAccounts,
  onGetExternalAccountsListAction,
  externalAccountsPagination,
  externalAccountsNextPageIsLoading,
  overdraftEnabled,
  recipientAccountsWithInfo,
  pspPartnersAccountsList,
  pspPartnerAccountBalanceInfo,
  pspPartnerAccountsPagination,
  pspPartnerAccountsNextPageIsLoading,
  pspPartnerAccountsOnScroll,
  clearErrors,
  setWithTime,
  withTime,
}: IncomingGeneralContentProps): React.JSX.Element {
  const [accountNumber, setAccountNumber] = useState('');
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [accountType, setAccountType] = useState<
    'internal' | 'external' | 'undefined'
  >();
  const [selectedExternalAccount, setSelelectedExternalAccount] = useState<
    ExternalAccount | undefined
  >(undefined);
  const [infoValue, setInfoValue] = useState('');

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  useEffect(() => {
    if (general && general.recipientAccountNumber) {
      setAccountNumber(general.recipientAccountNumber);
    }
  }, [general]);

  useEffect(() => {
    if (general && general.info) {
      setInfoValue(general.info);
    }
  }, [general]);

  useEffect(() => {
    if (
      general &&
      recipientAccounts?.length &&
      !recipientAccounts?.find(
        (item) => item.value === general.recipientAccountNumber,
      ) &&
      !accountType
    ) {
      setAccountType('undefined');
    }
  }, [general, baseellaStatus, recipientAccounts, accountType]);

  useEffect(() => {
    if (general && general.pspIntegrationAccountId && !accountType) {
      setAccountType('external');

      if (externalAccounts) {
        const currentAccount = externalAccounts?.find(
          (item) => item.id === general.pspIntegrationAccountId,
        );

        if (currentAccount) {
          setSelelectedExternalAccount(currentAccount);
        }
      }
    }
  }, [general, externalAccounts, accountType]);

  useEffect(() => {
    if (!general && !transactionId && !accountType) {
      setAccountType('internal');
    }
  }, [general, transactionId, accountType]);

  useEffect(() => {
    if (
      general &&
      !general.pspIntegrationAccountId &&
      general.recipientAccountNumber &&
      recipientAccounts?.find(
        (item) => item.value === general.recipientAccountNumber,
      ) &&
      !accountType
    ) {
      setAccountType('internal');
    }
  }, [general, accountType, recipientAccounts]);

  let currentRecipientsAccount;

  if (accountType !== 'external') {
    currentRecipientsAccount = recipientAccountsWithInfo?.find(
      (item) =>
        item.fullNumber === fields?.recipientAccountNumber ||
        item.fullNumber === general?.recipientAccountNumber,
    );
  } else {
    currentRecipientsAccount = externalAccounts?.find(
      (item) =>
        item.account.fullNumber === fields?.recipientAccountNumber ||
        item.account.fullNumber === general?.recipientAccountNumber,
    )?.account;
  }

  const currentBalance = balances?.find(
    (item) =>
      item.currencyId === fields?.currencyId ||
      item.currencyId === general?.currencyId,
  );

  const currentPspAccount = pspPartnersAccountsList?.find(
    (item) =>
      item.account.fullNumber === general?.pspPartnerAccountNumber ||
      item?.account?.fullNumber === fields?.pspPartnerAccountNumber,
  );

  let content;
  let availableCurrencies: SelectItemProps[] = [];

  if (accountType !== 'undefined') {
    if (selectedExternalAccount) {
      availableCurrencies =
        selectedExternalAccount.pspIntegrationPayment?.currencies?.map(
          (item) => ({
            name: item.currency.name,
            value: item.currency.id,
          }),
        ) as SelectItemProps[];
    } else {
      availableCurrencies = currencies as SelectItemProps[];

      if (
        !recipientAccounts?.find(
          (item) => item.value === fields?.recipientAccountNumber,
        )
      ) {
        availableCurrencies = currenciesList?.map((item) => ({
          name: item.name,
          value: item.id,
          suffix: item.description,
        })) as SelectItemProps[];
      }
    }
  } else if (accountType === 'undefined') {
    availableCurrencies = currenciesList?.map((item) => ({
      name: item.name,
      value: item.id,
      suffix: item.description,
    })) as SelectItemProps[];
  }

  const enableEdit = Boolean(control);

  if (isLoading) {
    content = (
      <div className={styles.loader}>
        <IconLoader className={styles.loaderIcon} />
      </div>
    );
  } else {
    let currencyValue = '';
    let pspPartnerValue = '';

    if (!control) {
      currencyValue = currencyName as string;
      pspPartnerValue = pspPartnerName as string;
    } else if (enableEdit) {
      currencyValue = general?.currencyId;
      pspPartnerValue = general?.pspPartnerId as string;
    }

    content = (
      <>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>General Info</div>
          {general && (
            <SidepanelDateRow
              title="Creation Date"
              value={enableEdit ? '' : formatDate(general?.createdAt || '')}
              name="createdAt"
              placeholder="Select Date"
              control={control}
              enableEdit={enableEdit}
              copyEnabled={Boolean(general)}
              copyValue={
                enableEdit
                  ? formatDate(fields?.createdAt || '')
                  : formatDate(general?.createdAt || '')
              }
              disabled
              tabIndex={-1}
              clearErrors={clearErrors}
              withTime
            />
          )}
          <SidepanelDateRow
            title="Transfer Date"
            value={enableEdit ? '' : formatDate(general?.transferDate || '')}
            name="transferDate"
            placeholder="Select Date"
            control={control}
            enableEdit={enableEdit}
            disabled={!isEditable || Boolean(general)}
            error={errors?.transferDate?.message}
            tabIndex={!isEditable || Boolean(general) ? -1 : 0}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? formatDate(fields?.transferDate || '')
                : formatDate(general?.transferDate || '')
            }
            clearErrors={clearErrors}
            setWithTime={setWithTime?.setTransferDateWithTime}
            withTime={withTime?.transferDateWithTime}
            // withShortcut
          />

          <SidepanelDateRow
            title="Accepted In"
            value={enableEdit ? '' : formatDate(general?.acceptedIn || '')}
            name="acceptedIn"
            placeholder="Select Accepted In"
            control={control}
            enableEdit={enableEdit}
            startDate={transferDate ? new Date(transferDate) : undefined}
            tabIndex={
              !isEditable ||
              !transferDate ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              !transferDate ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            error={errors?.acceptedIn?.message}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? formatDate(fields?.acceptedIn || '')
                : formatDate(general?.acceptedIn || '')
            }
            clearErrors={clearErrors}
            withTime={withTime?.accpetedInWithTime}
            setWithTime={setWithTime?.setAcceptedInWithTime}
          />
          <SidepanelDateRow
            title="Transaction Posting date"
            value={
              enableEdit
                ? ''
                : formatDate(general?.depositDateOfDestinationAccount || '')
            }
            name="depositDateOfDestinationAccount"
            placeholder="Select Transaction Posting date"
            control={control}
            enableEdit={enableEdit}
            tabIndex={!isEditable || !transferDate || Boolean(general) ? -1 : 0}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? formatDate(fields?.depositDateOfDestinationAccount || '')
                : formatDate(general?.depositDateOfDestinationAccount || '')
            }
            disabled={!isEditable || !transferDate || Boolean(general)}
            error={errors?.depositDateOfDestinationAccount?.message}
            clearErrors={clearErrors}
            withTime={withTime?.postingDateWithTime}
            setWithTime={setWithTime?.setPostingDateWithTime}
          />
          <div className={styles.accountsTypesSelectorWrapper}>
            <div className={styles.accountsTypesSelector}>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="internal"
                  checked={accountType === 'internal'}
                  title="Internal"
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      recipientAccounts?.find(
                        (item) => item.value === fields?.recipientAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('internal');
                    setSelelectedExternalAccount(undefined);

                    if (setValue && !transactionId) {
                      setValue('pspIntegrationAccountId', '');
                    }
                  }}
                />
              </div>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="external"
                  checked={accountType === 'external'}
                  title="External"
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      recipientAccounts?.find(
                        (item) => item.value === fields?.recipientAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('external');

                    if (
                      accountType !== 'external' &&
                      !fields.pspIntegrationAccountId &&
                      onSearchExternalAccounts
                    ) {
                      onSearchExternalAccounts('');
                    }

                    if (setValue && !transactionId) {
                      setValue('recipientAccountNumber', '');
                    }
                  }}
                />
              </div>
              <div className={styles.accountType}>
                <CheckboxInput
                  type="radio"
                  name="undefined"
                  checked={accountType === 'undefined'}
                  title="Undefined funds"
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      recipientAccounts?.find(
                        (item) => item.value === fields?.recipientAccountNumber,
                      ),
                    )
                  }
                  onChange={() => {
                    setAccountType('undefined');
                    setSelelectedExternalAccount(undefined);

                    if (setValue && !transactionId) {
                      setValue('pspIntegrationAccountId', '');
                    }
                  }}
                />
              </div>
            </div>
            {accountType === 'internal' && (
              <SidepanelAutocompleteRow
                title="Internal Beneficiary Account"
                value={enableEdit ? '' : general?.recipientAccountNumber}
                name="recipientAccountNumber"
                placeholder="Choose Internal Beneficiary Account"
                autocompleteList={recipientAccounts}
                isLoading={recipientAccountsIsLoading}
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    recipientAccounts?.find(
                      (item) => item.value === fields?.recipientAccountNumber,
                    ),
                  )
                }
                tabIndex={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    recipientAccounts?.find(
                      (item) => item.value === fields?.recipientAccountNumber,
                    ),
                  ) ||
                  Boolean(general)
                    ? -1
                    : 0
                }
                setValue={setValue}
                error={errors?.recipientAccountNumber?.message}
                onSearchAutocomplete={onSearchAutocomplete}
                copyEnabled={Boolean(general)}
                copyValue={
                  enableEdit
                    ? fields?.recipientAccountNumber
                    : general?.recipientAccountNumber
                }
                hasExternalAccountValue={Boolean(
                  fields?.pspIntegrationAccountId,
                )}
                clearErrors={clearErrors}
                withSuffix
              />
            )}
            {accountType === 'undefined' && (
              <SidepanelTextRow
                title="Beneficiary Account"
                value={enableEdit ? '' : general?.recipientAccountNumber}
                name="recipientAccountNumber"
                placeholder="Enter Beneficiary Account"
                control={control}
                fieldValue={accountNumber}
                setFieldValue={setAccountNumber}
                enableEdit={enableEdit}
                tabIndex={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    recipientAccounts?.find(
                      (item) => item.value === fields?.recipientAccountNumber,
                    ),
                  )
                    ? -1
                    : 0
                }
                disabled={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                    recipientAccounts?.find(
                      (item) => item.value === fields?.recipientAccountNumber,
                    ),
                  )
                }
                error={errors?.recipientAccountNumber?.message}
                copyEnabled={Boolean(general)}
                copyValue={
                  enableEdit
                    ? fields?.recipientAccountNumber
                    : general?.recipientAccountNumber
                }
                setValue={setValue}
                hasExternalAccountValue={Boolean(
                  fields?.pspIntegrationAccountId,
                )}
                clearErrors={clearErrors}
              />
            )}
            {accountType === 'external' &&
              activePermission?.includes(
                PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
              ) && (
                <ExternalAccountRow
                  title="External Beneficiary Account"
                  value={enableEdit ? '' : general?.pspIntegrationAccountId}
                  name="pspIntegrationAccountId"
                  placeholder="Choose External Beneficiary Account"
                  control={control}
                  enableEdit={enableEdit}
                  tabIndex={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      recipientAccounts?.find(
                        (item) => item.value === fields?.recipientAccountNumber,
                      ),
                    )
                      ? -1
                      : 0
                  }
                  disabled={
                    !isEditable ||
                    Boolean(
                      general &&
                      baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS &&
                      recipientAccounts?.find(
                        (item) => item.value === fields?.recipientAccountNumber,
                      ),
                    )
                  }
                  error={errors?.pspIntegrationAccountId?.message}
                  copyEnabled={Boolean(general)}
                  copyValue={
                    externalAccounts?.find(
                      (item) =>
                        item.id === fields.pspInegrationAccountId ||
                        item.id === general?.pspIntegrationAccountId,
                    )?.iban
                  }
                  externalAccounts={externalAccounts}
                  setValue={setValue}
                  isLoading={externalAccountsIsLoading}
                  selectedExternalAccount={selectedExternalAccount}
                  setSelelectedExternalAccount={setSelelectedExternalAccount}
                  pspIntegrationAccountId={fields?.pspIntegrationAccountId}
                  onSearch={onSearchExternalAccounts}
                  onClear={onClearExternalAccounts}
                  onGetList={onGetExternalAccountsListAction}
                  pagination={externalAccountsPagination}
                  transactionId={transactionId}
                  overdraftEnabled={overdraftEnabled}
                  externalAccountsNextPageIsLoading={
                    externalAccountsNextPageIsLoading
                  }
                  clearErrors={clearErrors}
                />
              )}
            {accountType === 'external' &&
              !activePermission?.includes(
                PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST,
              ) && (
                <div className={styles.noAccess}>
                  You don&apos;t have access to this section
                </div>
              )}
          </div>
          <div
            className={`${styles.amountRow} ${!control ? styles.noFlex : ''}`}
          >
            <div className={styles.amountRowItem}>
              <SidepanelAmountRow
                title="Amount"
                value={enableEdit ? '' : general?.sum.toFixed(2)}
                name="sum"
                placeholder="Enter amount"
                control={control}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
                  ) ||
                  (accountType !== 'undefined' &&
                    (!balances || (balances && !balances.length)))
                }
                error={errors?.sum?.message}
                tabIndex={
                  !isEditable ||
                  Boolean(general) ||
                  (accountType !== 'undefined' &&
                    (!balances || (balances && !balances.length)))
                    ? -1
                    : 0
                }
                amount={amount}
                setAmount={setAmount}
                currenciesList={currenciesList}
                currencyId={currencyId}
                copyEnabled={Boolean(general)}
                copyValue={enableEdit ? fields?.sum : general?.sum}
                clearErrors={clearErrors}
              />
            </div>
            <div className={styles.amountRowItem}>
              <SidepanelSelectRow
                title="Currency"
                value={currencyValue}
                name="currencyId"
                placeholder="Choose currency"
                control={control}
                selectList={availableCurrencies}
                enableEdit={enableEdit}
                disabled={
                  !isEditable ||
                  Boolean(
                    general &&
                    baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
                  ) ||
                  (accountType !== 'undefined' &&
                    (!balances || (balances && !balances.length)))
                }
                tabIndex={
                  !isEditable ||
                  Boolean(general) ||
                  (accountType !== 'undefined' &&
                    (!balances || (balances && !balances.length)))
                    ? -1
                    : 0
                }
                error={errors?.currencyId?.message}
                copyEnabled={Boolean(general)}
                copyValue={
                  enableEdit
                    ? availableCurrencies?.find(
                        (item) => item.value === currencyValue,
                      )?.name
                    : general?.currency?.name
                }
                clearErrors={clearErrors}
              />
            </div>
          </div>
          {!accountBalancesIsLoading && balances && !balances?.length && (
            <div className={`${styles.fee} ${styles.noCurrencies}`}>
              Account doesn&apos;t have available currencies
            </div>
          )}
          {accountBalancesIsLoading && (
            <div className={`${styles.fee} ${styles.noCurrencies}`}>
              Currencies and balances is loading...
            </div>
          )}
          {control && (
            <>
              {(!overdraftEnabled ||
                (currentRecipientsAccount &&
                  (!currentRecipientsAccount?.overdraftEnabled ||
                    !currentBalance?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${
                    general ? styles.balanceWrapperWithCopy : ''
                  }`}
                >
                  <InputText
                    disabled
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    value={
                      accountBalance !== undefined && currencyId
                        ? `${formatBalance(
                            accountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrencies?.find(
                              (item) => item.value === currencyId,
                            )?.name || ''
                          }`
                        : '-'
                    }
                    tabIndex={-1}
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() => {
                      if (selectedExternalAccount && updateExternalBalance) {
                        updateExternalBalance();
                      } else if (!selectedExternalAccount && updateBalance) {
                        updateBalance();
                      }
                    }}
                    className={`${styles.updateBalanceButton} ${
                      balanceIsLoading ? styles.loading : ''
                    } `}
                    disabled={accountBalance === undefined || !isEditable}
                    tabIndex={-1}
                  />
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          accountBalance !== undefined
                            ? accountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentRecipientsAccount?.overdraftEnabled &&
                currentBalance?.overdraftEnabled && (
                  <div className={styles.overdraftBalanceWrapper}>
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        general ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="balance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        labelClassName={styles.overdraftBalanceLabel}
                        value={
                          accountBalance !== undefined
                            ? `${formatBalance(
                                accountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() => {
                          if (
                            selectedExternalAccount &&
                            updateExternalBalance
                          ) {
                            updateExternalBalance();
                          } else if (
                            !selectedExternalAccount &&
                            updateBalance
                          ) {
                            updateBalance();
                          }
                        }}
                        className={`${styles.updateBalanceButton} ${
                          balanceIsLoading ? styles.loading : ''
                        } `}
                        disabled={accountBalance === undefined || !isEditable}
                        tabIndex={-1}
                      />
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              accountBalance !== undefined
                                ? accountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="availableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        labelClassName={styles.overdraftBalanceLabel}
                        value={
                          currentBalance && currentBalance?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                currentBalance?.value < 0
                                  ? (currentBalance?.overdraftValue as number) -
                                      Math.abs(currentBalance?.value) <
                                    0
                                    ? 0
                                    : (currentBalance?.overdraftValue as number) -
                                      Math.abs(currentBalance?.value)
                                  : (currentBalance?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="overdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        labelClassName={styles.overdraftBalanceLabel}
                        value={
                          currentBalance && currentBalance?.overdraftEnabled
                            ? `${formatBalance(
                                currentBalance?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentRecipientsAccount &&
                  (!currentRecipientsAccount?.overdraftEnabled ||
                    !currentBalance?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {accountBalance !== undefined
                    ? `${formatBalance(
                        accountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${general?.currency?.name}`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          accountBalance !== undefined
                            ? accountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentRecipientsAccount?.overdraftEnabled &&
                currentBalance?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {accountBalance !== undefined
                        ? `${formatBalance(
                            accountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${general?.currency?.name}`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              accountBalance !== undefined
                                ? accountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {currentBalance && currentBalance?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            currentBalance?.value < 0
                              ? (currentBalance?.overdraftValue as number) -
                                  Math.abs(currentBalance?.value) <
                                0
                                ? 0
                                : (currentBalance?.overdraftValue as number) -
                                  Math.abs(currentBalance?.value)
                              : (currentBalance?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft Limit">
                      {currentBalance && currentBalance?.overdraftEnabled
                        ? `${formatBalance(
                            currentBalance?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          {feeText && !transactionId && (
            <div className={styles.fee}>{feeText}</div>
          )}
        </div>

        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Details</div>
          <SidepanelSelectRow
            title="Payment type"
            value={
              enableEdit
                ? ''
                : paymentsTypes?.find(
                    (item) => item.value === general?.paymentType,
                  )?.name
            }
            name="paymentType"
            placeholder="Choose Payment Type"
            selectList={
              selectedExternalAccount
                ? selectedExternalAccount?.pspIntegrationPayment?.paymentsTypes?.map(
                    (item) => {
                      const title = item
                        .split('_')
                        .map(
                          (type) =>
                            `${type.charAt(0)}${type.slice(1).toLowerCase()}`,
                        )
                        ?.join(' ');

                      return { name: title, value: item };
                    },
                  )
                : paymentsTypes
            }
            control={control}
            enableEdit={enableEdit}
            disabled={
              !isEditable ||
              (Boolean(
                general && baseellaStatus !== BaseellaStatus.UNDEFINED_FUNDS,
              ) &&
                baseellaStatus !== BaseellaStatus.PROC) ||
              !currencyId
            }
            error={errors?.paymentType?.message}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? paymentsTypes?.find(
                    (item) => item.value === fields?.paymentType,
                  )?.name
                : paymentsTypes?.find(
                    (item) => item.value === general?.paymentType,
                  )?.name
            }
            tabIndex={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC) ||
              !currencyId
                ? -1
                : 0
            }
            withClearButton
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Partner Bank"
            value={pspPartnerValue}
            name="pspPartnerId"
            placeholder="Choose Partner Bank"
            autocompleteList={
              selectedExternalAccount
                ? ([
                    {
                      name: selectedExternalAccount?.pspPartner?.companyName,
                      value: selectedExternalAccount?.pspPartner?.id,
                    },
                  ] as SelectItemProps[])
                : pspPartners
            }
            isLoading={pspPartnersIsLoading}
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              !paymentTypeIsFilled ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              !paymentTypeIsFilled ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            onSearchAutocomplete={onSearchAutocomplete}
            error={errors?.pspPartnerId?.message}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? pspPartners?.find(
                    (item) => item.value === fields?.pspPartnerId,
                  )?.name
                : pspPartnerValue
            }
            withClearButton
            clearErrors={clearErrors}
          />
          <SidepanelAutocompleteRow
            title="Partner Bank Account"
            value={enableEdit ? '' : general?.pspPartnerAccountNumber}
            name="pspPartnerAccountNumber"
            placeholder="Choose Partner Bank Account"
            autocompleteList={pspPartnerAccounts}
            isLoading={pspPartnerAccountsIsLoading}
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              !pspPartnerIsFilled ||
              !activePermission?.includes(PermissionEnum.PSP_LIST) ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              !pspPartnerIsFilled ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC) ||
              !activePermission?.includes(PermissionEnum.PSP_LIST)
            }
            error={
              errors?.pspPartnerAccountNumber?.message ||
              (!activePermission?.includes(PermissionEnum.PSP_LIST)
                ? 'Data not loaded. Permission denied'
                : '')
            }
            onSearchAutocomplete={onSearchAutocomplete}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit
                ? fields?.pspPartnerAccountNumber
                : general?.pspPartnerAccountNumber
            }
            onScroll={pspPartnerAccountsOnScroll}
            nextPageIsLoading={pspPartnerAccountsNextPageIsLoading}
            pagination={pspPartnerAccountsPagination}
            withInfiniteScroll
            withSuffix
            clearErrors={clearErrors}
          />
          {control && (
            <>
              {(!overdraftEnabled ||
                (currentPspAccount &&
                  (!currentPspAccount?.account?.overdraftEnabled ||
                    !pspPartnerAccountBalanceInfo?.overdraftEnabled))) && (
                <div
                  className={`${styles.balanceWrapper} ${styles.moreMargin} ${
                    general ? styles.balanceWrapperWithCopy : ''
                  }`}
                >
                  <InputText
                    disabled
                    name="balance"
                    placeholder=""
                    title="Current balance"
                    tabIndex={-1}
                    value={
                      pspPartnerAccountBalance !== undefined
                        ? `${formatBalance(
                            pspPartnerAccountBalance,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            availableCurrencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'
                    }
                  />
                  <ButtonPseudo
                    icon={<IconRefresh />}
                    onClick={() =>
                      updatePspPartnerBalance && updatePspPartnerBalance()
                    }
                    className={`${styles.updateBalanceButton} ${
                      pspPartnerBalanceIsLoading ? styles.loading : ''
                    } `}
                    disabled={
                      pspPartnerAccountBalance === undefined || !isEditable
                    }
                    tabIndex={-1}
                  />
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.copyButton} ${
                        styles.autocompleteCopyButton
                      } ${copyIsSuccess ? styles.success : ''}`}
                      onClick={() =>
                        onCopy(
                          pspPartnerAccountBalance !== undefined
                            ? pspPartnerAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </div>
              )}
              {overdraftEnabled &&
                currentPspAccount?.account?.overdraftEnabled &&
                pspPartnerAccountBalanceInfo?.overdraftEnabled && (
                  <div
                    className={`${styles.overdraftBalanceWrapper} ${styles.withMargin}`}
                  >
                    <div
                      className={`${styles.overdraftBalanceValue} ${
                        general ? styles.withCopy : ''
                      }`}
                    >
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerBalance"
                        placeholder=""
                        title="Total available balance"
                        className={styles.overdraftBalance}
                        value={
                          pspPartnerAccountBalance !== undefined
                            ? `${formatBalance(
                                pspPartnerAccountBalance,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                      <ButtonPseudo
                        icon={<IconRefresh />}
                        onClick={() =>
                          updatePspPartnerBalance && updatePspPartnerBalance()
                        }
                        className={`${styles.updateBalanceButton} ${
                          balanceIsLoading ? styles.loading : ''
                        } `}
                        disabled={
                          pspPartnerAccountBalance === undefined || !isEditable
                        }
                        tabIndex={-1}
                      />
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.copyButton} ${
                            styles.autocompleteCopyButton
                          } ${copyIsSuccess ? styles.success : ''}`}
                          onClick={() =>
                            onCopy(
                              pspPartnerAccountBalance !== undefined
                                ? pspPartnerAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerAvailableOverdraft"
                        placeholder=""
                        title="Remaining overdraft"
                        value={
                          pspPartnerAccountBalanceInfo &&
                          pspPartnerAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                // eslint-disable-next-line no-nested-ternary
                                pspPartnerAccountBalanceInfo?.value < 0
                                  ? (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(
                                        pspPartnerAccountBalanceInfo?.value,
                                      ) <
                                    0
                                    ? 0
                                    : (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                      Math.abs(
                                        pspPartnerAccountBalanceInfo?.value,
                                      )
                                  : (pspPartnerAccountBalanceInfo?.overdraftValue as number),
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                    <div className={styles.overdraftBalanceValue}>
                      <InputText
                        disabled
                        tabIndex={-1}
                        name="pspPartnerOverdraftLimit"
                        placeholder=""
                        title="Overdraft limit"
                        value={
                          pspPartnerAccountBalanceInfo &&
                          pspPartnerAccountBalanceInfo?.overdraftEnabled
                            ? `${formatBalance(
                                pspPartnerAccountBalanceInfo?.overdraftValue as number,
                                currenciesList?.find(
                                  (item) => item.id === currencyId,
                                ),
                              )} ${
                                currencies?.find(
                                  (item) => item.value === currencyId,
                                )?.name
                              }`
                            : '-'
                        }
                      />
                    </div>
                  </div>
                )}
            </>
          )}
          {!control && (
            <>
              {(!overdraftEnabled ||
                (currentPspAccount &&
                  (!currentPspAccount?.account?.overdraftEnabled ||
                    !pspPartnerAccountBalanceInfo?.overdraftEnabled))) && (
                <SidepanelDefaultRow
                  title="Current balance"
                  valueClassName={general ? styles.readCopyButtonWrapper : ''}
                >
                  {pspPartnerAccountBalance !== undefined
                    ? `${formatBalance(
                        pspPartnerAccountBalance,
                        currenciesList?.find((item) => item.id === currencyId),
                      )} ${general?.currency?.name}`
                    : '-'}
                  {Boolean(general) && (
                    <button
                      type="button"
                      className={`${styles.readCopyButton} ${
                        copyIsSuccess ? styles.success : ''
                      }`}
                      onClick={() =>
                        onCopy(
                          pspPartnerAccountBalance !== undefined
                            ? pspPartnerAccountBalance?.toString()
                            : '-',
                        )
                      }
                      aria-label="Copy"
                    >
                      <IconCopy />
                    </button>
                  )}
                </SidepanelDefaultRow>
              )}
              {overdraftEnabled &&
                currentPspAccount?.overdraftEnabled &&
                pspPartnerAccountBalanceInfo?.overdraftEnabled && (
                  <>
                    <SidepanelDefaultRow
                      title="Total available balance"
                      valueClassName={
                        general ? styles.readCopyButtonWrapper : ''
                      }
                    >
                      {pspPartnerAccountBalance !== undefined
                        ? `${formatBalance(pspPartnerAccountBalance)} ${
                            general?.currency?.name
                          }`
                        : '-'}
                      {Boolean(general) && (
                        <button
                          type="button"
                          className={`${styles.readCopyButton} ${
                            copyIsSuccess ? styles.success : ''
                          }`}
                          onClick={() =>
                            onCopy(
                              pspPartnerAccountBalance !== undefined
                                ? pspPartnerAccountBalance?.toString()
                                : '-',
                            )
                          }
                          aria-label="Copy"
                        >
                          <IconCopy />
                        </button>
                      )}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Remaining overdraft">
                      {pspPartnerAccountBalanceInfo &&
                      pspPartnerAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            // eslint-disable-next-line no-nested-ternary
                            pspPartnerAccountBalanceInfo?.value < 0
                              ? (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(
                                    pspPartnerAccountBalanceInfo?.value,
                                  ) <
                                0
                                ? 0
                                : (pspPartnerAccountBalanceInfo?.overdraftValue as number) -
                                  Math.abs(pspPartnerAccountBalanceInfo?.value)
                              : (pspPartnerAccountBalanceInfo?.overdraftValue as number),
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                    <SidepanelDefaultRow title="Overdraft limit">
                      {pspPartnerAccountBalanceInfo &&
                      pspPartnerAccountBalanceInfo?.overdraftEnabled
                        ? `${formatBalance(
                            pspPartnerAccountBalanceInfo?.overdraftValue as number,
                            currenciesList?.find(
                              (item) => item.id === currencyId,
                            ),
                          )} ${
                            currencies?.find(
                              (item) => item.value === currencyId,
                            )?.name
                          }`
                        : '-'}
                    </SidepanelDefaultRow>
                  </>
                )}
            </>
          )}
          <SidepanelTextRow
            title="Document #"
            value={enableEdit ? '' : general?.documentNumber}
            name="documentNumber"
            placeholder="Enter Document"
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            error={errors?.documentNumber?.message}
            copyEnabled={Boolean(general)}
            copyValue={
              enableEdit ? fields?.documentNumber : general?.documentNumber
            }
            clearErrors={clearErrors}
          />
        </div>
        <div className={styles.transactionBlockWrapper}>
          <div className={styles.transactionTitle}>Payment details</div>
          <SidepanelTextareaRow
            title="Payment Details"
            value={enableEdit ? '' : general?.info}
            name="info"
            placeholder="Enter Payment Details"
            control={control}
            enableEdit={enableEdit}
            tabIndex={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
                ? -1
                : 0
            }
            disabled={
              !isEditable ||
              (Boolean(general) && baseellaStatus !== BaseellaStatus.PROC)
            }
            error={errors?.info?.message}
            copyEnabled={Boolean(general)}
            copyValue={enableEdit ? fields?.info : general?.info}
            fieldValue={infoValue}
            setFieldValue={setInfoValue}
            maxLength={140}
            clearErrors={clearErrors}
          />
        </div>
        {withTransactionUrl && (
          <NavLink
            to={`/transactions/${transactionUrlType}/${transactionId}`}
            target="_blank"
            className={`${styles.transactionLink} ${styles.withMargin}`}
            tabIndex={-1}
          >
            Go to transaction
          </NavLink>
        )}
      </>
    );
  }

  return content;
}
